import { KeyboardEvent, ReactNode, useState } from 'react'
import { useTheme, type SxProps, type Theme } from '@mui/material'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import Drawer from '@mui/material/Drawer'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { ArrowUpRight as ArrowUpRightIcon } from '@phosphor-icons/react/dist/ssr/ArrowUpRight'
import { CaretDown as CaretDownIcon } from '@phosphor-icons/react/dist/ssr/CaretDown'
import { CaretRight as CaretRightIcon } from '@phosphor-icons/react/dist/ssr/CaretRight'
import { Link as RouterLink } from 'react-router-dom'

import type { NavItemConfig } from '@/types/nav'
import { paths } from '@/paths'
import { isNavItemActive } from '@/lib/is-nav-item-active'
import { usePathname } from '@/hooks/use-pathname'
import { useSettings } from '@/hooks/use-settings'
import { useWorkspaces } from '@/hooks/use-workspaces'
import { Logo } from '@/components/core/logo'

import { logoColors, sideNavColorStyles } from '../styles'
import { WorkspacesSwitch } from '../workspaces/workspaces-switch'
import { icons } from './nav-icons'
import { NavTypeformItem } from './nav-typeform-item'
import { SidebarIcon } from './sidebar-toggle-icons'
import { useStatusColor } from './status-indicator'

export interface SideNavProps {
	items?: NavItemConfig[]
	mini: boolean
	toggleMini: () => void
}

const SIDE_NAV_HEADER_HEIGHT = 'var(--SideNav-header-height)'

export function SideNav({ items = [], mini, toggleMini }: SideNavProps) {
	const pathname = usePathname()
	const theme = useTheme()

	const {
		settings: { colorScheme = 'light' },
	} = useSettings()

	const styles = sideNavColorStyles[colorScheme]
	const logoColor = logoColors[colorScheme]

	return (
		<Drawer
			PaperProps={{
				'data-test': 'side-nav',
				className: mini ? 'SideNav--mini' : undefined,
				sx: {
					...styles,
					marginTop: 'calc(var(--ImpersonationBanner-height, 0px) + var(--BetterStackBanner-height, 0px))',
					bgcolor: 'var(--SideNav-background)',
					borderRight: 'var(--SideNav-border)',
					color: 'var(--SideNav-color)',
					display: { xs: 'none', lg: 'flex' },
					flexDirection: 'column',
					height: '100%',
					left: 0,
					position: 'fixed',
					top: 0,
					width: 'var(--SideNav-width)',
					zIndex: 'var(--SideNav-zIndex)',
					overflow: 'visible',
					transition: theme.transitions.create('width', {
						easing: theme.transitions.easing.sharp,
						duration: theme.transitions.duration.standard,
					}),
					...(mini && {
						'&:hover .MiniSidebarLogoToggle-emblem, &:focus-within .MiniSidebarLogoToggle-emblem': {
							opacity: 0,
							pointerEvents: 'none',
						},
						'&:hover .MiniSidebarLogoToggle-button, &:focus-within .MiniSidebarLogoToggle-button': {
							opacity: 1,
							pointerEvents: 'auto',
						},
					}),
				},
			}}
			variant="permanent"
		>
			<Stack spacing={3} sx={{ pt: 'var(--SideNav-header-padding-top)', px: 2, pb: 2 }}>
				<Stack
					direction="row"
					alignItems="center"
					justifyContent={mini ? 'center' : 'space-between'}
					sx={{ gap: 0.5, height: SIDE_NAV_HEADER_HEIGHT, minHeight: SIDE_NAV_HEADER_HEIGHT, width: '100%' }}
				>
					<Box
						sx={{
							alignItems: 'center',
							display: 'flex',
							flex: mini ? '1 1 auto' : '0 1 auto',
							height: SIDE_NAV_HEADER_HEIGHT,
							justifyContent: 'center',
						}}
					>
						{mini ? (
							<MiniSidebarLogoToggle logoColor={logoColor} onToggle={toggleMini} />
						) : (
							<Box component={RouterLink} to={paths.home} sx={{ display: 'inline-flex', lineHeight: 0 }}>
								<Logo color={logoColor} height={31.5} width="auto" emblem={false} />
							</Box>
						)}
					</Box>
					{!mini ? <SidebarToggleButton mini={mini} onToggle={toggleMini} /> : null}
				</Stack>
				<WorkspacesSwitch mini={mini} />
			</Stack>
			<Box
				component="nav"
				sx={{
					flex: '1 1 auto',
					overflowY: 'auto',
					py: 2,
					px: mini ? 1 : 2,
					scrollbarWidth: 'none',
					'&::-webkit-scrollbar': { display: 'none' },
					transition: theme.transitions.create(['padding-left', 'padding-right'], {
						easing: theme.transitions.easing.sharp,
						duration: theme.transitions.duration.standard,
					}),
				}}
			>
				{renderNavGroups({ items, pathname, mini, toggleMini })}
			</Box>
		</Drawer>
	)
}

const sidebarToggleButtonSx = {
	flexShrink: 0,
	marginRight: -0.5,
	marginBottom: 0.25,
	color: 'var(--SideNav-color)',
	'&:hover': { bgcolor: 'action.hover' },
}

function SidebarToggleButton({ mini, onToggle, sx }: { mini: boolean; onToggle: () => void; sx?: SxProps<Theme> }) {
	const label = mini ? 'Expand sidebar' : 'Collapse sidebar'

	return (
		<Tooltip title={label} placement="right">
			<IconButton
				onClick={onToggle}
				aria-label={label}
				data-test="sidebar-toggle"
				aria-expanded={!mini}
				sx={{
					...sidebarToggleButtonSx,
					cursor: 'pointer',
					...sx,
				}}
			>
				<SidebarIcon size="1.75rem" />
			</IconButton>
		</Tooltip>
	)
}

function MiniSidebarLogoToggle({
	logoColor,
	onToggle,
}: {
	logoColor: (typeof logoColors)[keyof typeof logoColors]
	onToggle: () => void
}) {
	const label = 'Expand sidebar'

	return (
		<Box
			sx={{
				display: 'grid',
				height: SIDE_NAV_HEADER_HEIGHT,
				placeItems: 'center',
				width: SIDE_NAV_HEADER_HEIGHT,
				'& > *': { gridArea: '1 / 1' },
			}}
		>
			<Tooltip title="Home" placement="right">
				<Box
					className="MiniSidebarLogoToggle-emblem"
					component={RouterLink}
					to={paths.home}
					sx={{
						display: 'inline-flex',
						lineHeight: 0,
						transition: 'opacity 150ms ease-in-out',
					}}
				>
					<Logo color={logoColor} height={SIDE_NAV_HEADER_HEIGHT} width={SIDE_NAV_HEADER_HEIGHT} emblem />
				</Box>
			</Tooltip>
			<Tooltip title={label} placement="right">
				<IconButton
					className="MiniSidebarLogoToggle-button"
					onClick={onToggle}
					aria-label={label}
					data-test="sidebar-expand"
					aria-expanded={false}
					sx={{
						margin: 0,
						opacity: 0,
						pointerEvents: 'none',
						borderRadius: 1,
						color: 'var(--SideNav-color)',
						transition: 'opacity 150ms ease-in-out',
						zIndex: 1,
						'&:hover': { bgcolor: 'action.hover' },
					}}
				>
					<SidebarIcon size="1.75rem" />
				</IconButton>
			</Tooltip>
		</Box>
	)
}

function renderNavGroups({
	items,
	pathname,
	mini,
	toggleMini,
}: {
	items: NavItemConfig[]
	pathname: string
	mini: boolean
	toggleMini: () => void
}) {
	const children = items.reduce((acc: ReactNode[], curr: NavItemConfig): ReactNode[] => {
		acc.push(
			<Stack component="li" key={curr.key} spacing={1.5}>
				{curr.title && !mini ? (
					<div>
						<Typography sx={{ color: 'var(--NavGroup-title-color)', fontSize: '0.875rem', fontWeight: 500 }}>
							{curr.title}
						</Typography>
					</div>
				) : null}
				<div>{renderNavItems({ depth: 0, items: curr.items, pathname, mini, toggleMini })}</div>
			</Stack>
		)

		return acc
	}, [])

	return (
		<Stack component="ul" spacing={2} sx={{ listStyle: 'none', m: 0, p: 0 }}>
			{children}
		</Stack>
	)
}

function renderNavItems({
	depth = 0,
	items = [],
	pathname,
	mini = false,
	toggleMini,
}: {
	depth: number
	items?: NavItemConfig[]
	pathname: string
	mini: boolean
	toggleMini: () => void
}) {
	const children = items.reduce((acc: ReactNode[], curr: NavItemConfig): ReactNode[] => {
		const { items: childItems, key, ...item } = curr

		if (curr.typeformId) {
			acc.push(<NavTypeformItem key={key} typeformId={curr.typeformId} title={curr.title} depth={depth} mini={mini} />)
			return acc
		}

		const forceOpen = childItems
			? Boolean(childItems.find((childItem) => childItem.href && pathname.startsWith(childItem.href)))
			: false

		acc.push(
			<NavItem
				depth={depth}
				forceOpen={forceOpen}
				key={key}
				pathname={pathname}
				mini={mini}
				toggleMini={toggleMini}
				{...item}
			>
				{childItems ? renderNavItems({ depth: depth + 1, pathname, items: childItems, mini, toggleMini }) : null}
			</NavItem>
		)

		return acc
	}, [])

	return (
		<Stack component="ul" data-depth={depth} spacing={1} sx={{ listStyle: 'none', m: 0, p: 0 }}>
			{children}
		</Stack>
	)
}

interface NavItemProps extends Omit<NavItemConfig, 'items'> {
	children?: ReactNode
	depth: number
	forceOpen?: boolean
	pathname: string
	mini: boolean
	toggleMini: () => void
}

function NavItem({
	children,
	depth,
	disabled,
	external,
	forceOpen = false,
	href,
	icon,
	label,
	matcher,
	pathname,
	title,
	mini,
	toggleMini,
	expandNavWhenMini = false,
	requiresBetaAccess,
	comingSoon = false,
	statusIndicator = false,
}: NavItemProps) {
	const [open, setOpen] = useState<boolean>(forceOpen)
	const { hasBetaFeaturesEnabled } = useWorkspaces()
	const statusColor = useStatusColor()

	if (requiresBetaAccess && !hasBetaFeaturesEnabled) {
		/* eslint-disable no-param-reassign -- Expected */
		disabled = true
		comingSoon = true
		href = undefined
		/* eslint-enable no-param-reassign -- Expected */
	}

	const active = isNavItemActive({ disabled, external, href, matcher, pathname })
	const Icon = icon ? icons[icon] : null
	const ExpandIcon = open ? CaretDownIcon : CaretRightIcon
	const isBranch = children && !href
	const expandNavOnClick = mini && expandNavWhenMini
	const showChildren = Boolean(children && open)
	const showExpandIcon = isBranch && !expandNavOnClick
	const tooltip = comingSoon ? 'Please reach out to support' : mini ? title : ''
	const showLabel = requiresBetaAccess ? hasBetaFeaturesEnabled : true

	return (
		<Tooltip title={tooltip} placement="right-start">
			<Box component="li" data-depth={depth} sx={{ userSelect: 'none' }}>
				<Box
					className="SideNav-navItem"
					data-test="side-nav-item"
					{...(isBranch
						? {
								onClick: (): void => {
									if (expandNavOnClick) {
										setOpen(true)
										toggleMini()
										return
									}
									setOpen(!open)
								},
								onKeyUp: (event: KeyboardEvent<HTMLDivElement>): void => {
									if (event.key === 'Enter' || event.key === ' ') {
										if (expandNavOnClick) {
											setOpen(true)
											toggleMini()
											return
										}
										setOpen(!open)
									}
								},
								role: 'button',
							}
						: {
								...(href
									? {
											component: RouterLink,
											to: href,
											target: external ? '_blank' : undefined,
											rel: external ? 'noreferrer' : undefined,
										}
									: { role: 'button' }),
							})}
					sx={{
						alignItems: 'center',
						borderRadius: 1,
						color: 'var(--NavItem-color)',
						cursor: 'pointer',
						display: 'flex',
						flex: '0 0 auto',
						gap: 1,
						p: '6px 16px',
						position: 'relative',
						textDecoration: 'none',
						whiteSpace: 'nowrap',
						...(disabled && {
							bgcolor: 'var(--NavItem-disabled-background)',
							color: 'var(--NavItem-disabled-color)',
							cursor: 'not-allowed',
						}),
						...(active && {
							bgcolor: 'var(--NavItem-active-background)',
							color: 'var(--NavItem-active-color)',
							...(depth > 0 && {
								'&::before': {
									bgcolor: 'var(--NavItem-children-indicator)',
									borderRadius: '2px',
									content: '" "',
									height: '20px',
									left: '-14px',
									position: 'absolute',
									width: '3px',
								},
							}),
						}),
						...(open && { color: 'var(--NavItem-open-color)' }),
						'& .NavItem-externalIcon': {
							opacity: 0,
							transition: 'opacity 150ms ease-in-out',
						},
						'&:hover': {
							...(!disabled &&
								!active && {
									bgcolor: 'var(--NavItem-hover-background)',
									color: 'var(--NavItem-hover-color)',
								}),
							'& .NavItem-externalIcon': {
								opacity: 1,
							},
						},
					}}
					tabIndex={0}
				>
					<Box sx={{ alignItems: 'center', display: 'flex', justifyContent: 'center', flex: '0 0 auto' }}>
						{Icon ? (
							<Icon
								fill={
									statusIndicator
										? statusColor
										: active
											? 'var(--NavItem-icon-active-color)'
											: 'var(--NavItem-icon-color)'
								}
								fontSize="var(--icon-fontSize-md)"
								weight={forceOpen || active ? 'fill' : undefined}
							/>
						) : null}
					</Box>
					<Box sx={{ flex: '1 1 auto', height: '28px', minWidth: 0 }}>
						{!mini ? (
							<Typography
								component="span"
								noWrap
								sx={{ color: 'inherit', fontSize: '0.875rem', fontWeight: 500, lineHeight: '28px' }}
							>
								{title}
							</Typography>
						) : null}
					</Box>
					{external && !mini ? (
						<Box
							className="NavItem-externalIcon"
							sx={{ alignItems: 'center', display: 'flex', flex: '0 0 auto', ml: 'auto' }}
						>
							<ArrowUpRightIcon fontSize="var(--icon-fontSize-sm)" />
						</Box>
					) : null}
					{showLabel && label && !mini ? <Chip color="primary" label={label} size="small" /> : null}

					{showExpandIcon ? (
						<Box sx={{ alignItems: 'center', display: 'flex', flex: '0 0 auto' }}>
							<ExpandIcon color="var(--NavItem-expand-color)" fontSize="var(--icon-fontSize-sm)" />
						</Box>
					) : null}
				</Box>
				{showChildren && !mini ? (
					<Box sx={{ pl: '24px' }}>
						<Box sx={{ borderLeft: '1px solid var(--NavItem-children-border)', pl: '12px' }}>{children}</Box>
					</Box>
				) : null}
			</Box>
		</Tooltip>
	)
}
